/**
 * Índices Map+Set para lookups O(1) (rollout via feature flag).
 */

import type { QueueItem, QueuePriority, QueueStatus } from './types';
import { getQueuePartition, type QueuePartition } from './partitioning';
import { getGroupKey } from './types';
import { getMaxAttemptsForItem } from '../retry/retryPolicy';

export type RetryIndexState = 'ready' | 'waiting' | 'exhausted';

let useQueueIndex = false;

export function setUseQueueIndex(enabled: boolean): void {
  useQueueIndex = enabled;
}

export function isQueueIndexEnabled(): boolean {
  return useQueueIndex;
}

export class QueueIndex {
  private itemsById = new Map<string, QueueItem>();
  private idToArrayIndex = new Map<string, number>();
  private byStatus = new Map<QueueStatus, Set<string>>();
  private byPriority = new Map<QueuePriority, Set<string>>();
  private byPartition = new Map<QueuePartition, Set<string>>();
  private byRetryState = new Map<RetryIndexState, Set<string>>();
  private byTransaction = new Map<string, Set<string>>();
  private byJourney = new Map<string, Set<string>>();

  rebuild(items: QueueItem[]): void {
    this.itemsById.clear();
    this.idToArrayIndex.clear();
    this.byStatus.clear();
    this.byPriority.clear();
    this.byPartition.clear();
    this.byRetryState.clear();
    this.byTransaction.clear();
    this.byJourney.clear();
    items.forEach((item, index) => {
      this.idToArrayIndex.set(item.id, index);
      this.add(item);
    });
  }

  add(item: QueueItem, arrayIndex?: number): void {
    this.itemsById.set(item.id, item);
    if (arrayIndex !== undefined) this.idToArrayIndex.set(item.id, arrayIndex);
    this.addToSet(this.byStatus, item.status, item.id);
    this.addToSet(this.byPriority, item.priority, item.id);
    this.addToSet(this.byPartition, getQueuePartition(item), item.id);
    this.addToSet(this.byRetryState, getRetryIndexState(item), item.id);
    this.addToSet(this.byTransaction, getGroupKey(item), item.id);
    this.addToSet(this.byJourney, item.params.sessionId, item.id);
  }

  updateItem(item: QueueItem, arrayIndex: number): void {
    this.remove(item.id);
    this.add(item, arrayIndex);
  }

  remove(id: string): void {
    const item = this.itemsById.get(id);
    if (!item) return;
    this.itemsById.delete(id);
    this.idToArrayIndex.delete(id);
    this.byStatus.get(item.status)?.delete(id);
    this.byPriority.get(item.priority)?.delete(id);
    this.byPartition.get(getQueuePartition(item))?.delete(id);
    this.byRetryState.get(getRetryIndexState(item))?.delete(id);
    this.byTransaction.get(getGroupKey(item))?.delete(id);
    this.byJourney.get(item.params.sessionId)?.delete(id);
  }

  getById(id: string): QueueItem | undefined {
    return this.itemsById.get(id);
  }

  findArrayIndex(id: string): number {
    return this.idToArrayIndex.get(id) ?? -1;
  }

  toArray(): QueueItem[] {
    return Array.from(this.itemsById.values());
  }

  filterByStatus(status: QueueStatus): QueueItem[] {
    const ids = this.byStatus.get(status);
    if (!ids) return [];
    return Array.from(ids)
      .map(id => this.itemsById.get(id)!)
      .filter(Boolean);
  }

  filterByStatusAndRetryReady(status: QueueStatus, now: number): QueueItem[] {
    return this.filterByStatus(status).filter(
      i => i.status === status && i.nextRetryAt <= now
    );
  }

  filterByRetryState(state: RetryIndexState, now: number): QueueItem[] {
    const ids = this.byRetryState.get(state);
    if (!ids) return [];
    return Array.from(ids)
      .map(id => this.itemsById.get(id)!)
      .filter(i => i && getRetryIndexState(i, now) === state);
  }

  hasId(id: string): boolean {
    return this.itemsById.has(id);
  }

  private addToSet<K>(map: Map<K, Set<string>>, key: K, id: string): void {
    if (!map.has(key)) map.set(key, new Set());
    map.get(key)!.add(id);
  }
}

let sharedIndex: QueueIndex | null = null;

export function getQueueIndex(): QueueIndex {
  if (!sharedIndex) sharedIndex = new QueueIndex();
  return sharedIndex;
}

export function syncIndexFromArray(items: QueueItem[]): QueueItem[] {
  if (!useQueueIndex) return items;
  getQueueIndex().rebuild(items);
  return items;
}

export function getRetryIndexState(
  item: QueueItem,
  now = Date.now()
): RetryIndexState {
  if (item.attempts >= getMaxAttemptsForItem(item)) return 'exhausted';
  if (item.nextRetryAt > now) return 'waiting';
  return 'ready';
}

export function rebuildQueueIndexFromArray(items: QueueItem[]): void {
  if (!useQueueIndex) return;
  getQueueIndex().rebuild(items);
}

export function validateQueueIndex(
  items: QueueItem[]
): { ok: boolean; mismatches: string[] } {
  if (!useQueueIndex) return { ok: true, mismatches: [] };
  const idx = getQueueIndex();
  const mismatches: string[] = [];
  for (const status of ['queued', 'processing', 'failed', 'sent', 'dead'] as QueueStatus[]) {
    const fromIndex = idx.filterByStatus(status).length;
    const fromArray = items.filter(i => i.status === status).length;
    if (fromIndex !== fromArray) {
      mismatches.push(`status:${status} index=${fromIndex} array=${fromArray}`);
    }
  }
  const now = Date.now();
  for (const state of ['ready', 'waiting', 'exhausted'] as RetryIndexState[]) {
    const fromIndex = idx.filterByRetryState(state, now).length;
    const fromArray = items.filter(i => getRetryIndexState(i, now) === state).length;
    if (fromIndex !== fromArray) {
      mismatches.push(`retry:${state} index=${fromIndex} array=${fromArray}`);
    }
  }
  for (const item of items) {
    if (!idx.hasId(item.id)) {
      mismatches.push(`missing_id:${item.id}`);
      break;
    }
  }
  return { ok: mismatches.length === 0, mismatches };
}

export function _testResetQueueIndex(): void {
  sharedIndex = null;
  useQueueIndex = false;
}
